import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ownerDelegationService } from "@/services/delegation";
import type { CreateDelegationInput } from "@/types";

const key = (applicantProcessId: string, formId: string) => [
  "delegation",
  applicantProcessId,
  formId,
];

export const useDelegationForForm = (
  applicantProcessId: string,
  formId: string,
  enabled = true
) =>
  useQuery({
    queryKey: key(applicantProcessId, formId),
    queryFn: () => ownerDelegationService.getForForm(applicantProcessId, formId),
    enabled: enabled && !!applicantProcessId && !!formId,
  });

export const useCreateDelegation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDelegationInput) =>
      ownerDelegationService.create(input),
    onSuccess: (_d, input) =>
      qc.invalidateQueries({
        queryKey: key(input.applicantProcessId, input.formId),
      }),
  });
};

export const useApproveDelegation = (applicantProcessId: string, formId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      comment?: string;
      responses?: unknown;
      nextStaffId?: string;
    }) => ownerDelegationService.approve(args.id, args),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: key(applicantProcessId, formId) }),
  });
};

export const useRequestDelegationChanges = (
  applicantProcessId: string,
  formId: string
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; comment: string }) =>
      ownerDelegationService.requestChanges(args.id, args.comment),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: key(applicantProcessId, formId) }),
  });
};

export const useResendDelegation = (applicantProcessId: string, formId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; recipientId: string }) =>
      ownerDelegationService.resend(args.id, args.recipientId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: key(applicantProcessId, formId) }),
  });
};

export const useCancelDelegation = (applicantProcessId: string, formId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason?: string }) =>
      ownerDelegationService.cancel(args.id, args.reason),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: key(applicantProcessId, formId) }),
  });
};

export const useAssignedToMe = () =>
  useQuery({
    queryKey: ["delegations", "assigned-to-me"],
    queryFn: () => ownerDelegationService.assignedToMe(),
    // Kept fresh so the queue badge and list reflect new assignments.
    refetchInterval: 60_000,
  });

export const useMyDelegation = (id: string) =>
  useQuery({
    queryKey: ["delegation", "mine", id],
    queryFn: () => ownerDelegationService.viewMine(id),
    enabled: !!id,
  });

export const useSaveMyDelegation = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (responses: unknown) => ownerDelegationService.saveMine(id, responses),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["delegation", "mine", id] }),
  });
};

export const useSubmitMyDelegation = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (responses: unknown) => ownerDelegationService.submitMine(id, responses),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delegation", "mine", id] });
      qc.invalidateQueries({ queryKey: ["delegations", "assigned-to-me"] });
    },
  });
};

export const useUpdateRecipients = (applicantProcessId: string, formId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      recipients: { email: string; name?: string; role: "EDITOR" | "VIEWER" }[];
    }) => ownerDelegationService.updateRecipients(args.id, args.recipients),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: key(applicantProcessId, formId) }),
  });
};
